'use client';

import { c as _c } from "react/compiler-runtime";
import type { CustomAgentChangeProposal, CustomAgentDraft, CustomAgentSummary } from '@/app/(chat)/api/chat/chat-tools/custom-agent-tools';
import type { ProposalDiffRow } from './interactive-proposal-card';
import { InteractiveProposalCard, ProposalDetailRow, ProposalDiff, ProposalStatusPill } from './interactive-proposal-card';
import { useCustomAgentChangeProposalAction } from './use-custom-agent-change-proposal-action';
type CustomAgentChangeProposalBlockProps = {
  proposal: CustomAgentChangeProposal;
};
type DiffRowCandidate = ProposalDiffRow & {
  changed: boolean;
};
function formatBoolean(value: boolean): string {
  return value ? 'Enabled' : 'Disabled';
}
function formatOptional(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed || 'None';
}
function getDiffRows(before: CustomAgentSummary, after: CustomAgentDraft): ProposalDiffRow[] {
  const rows: DiffRowCandidate[] = [{
    label: 'Title',
    before: before.title,
    after: after.title,
    changed: before.title !== after.title
  }, {
    label: 'Instructions',
    before: before.description,
    after: after.description,
    changed: before.description !== after.description,
    multiline: true
  }, {
    label: 'State',
    before: formatBoolean(before.enabled),
    after: formatBoolean(after.enabled),
    changed: before.enabled !== after.enabled
  }, {
    label: 'Include patterns',
    before: formatOptional(before.includePatterns),
    after: formatOptional(after.includePatterns),
    changed: (before.includePatterns ?? null) !== after.includePatterns
  }, {
    label: 'Exclude patterns',
    before: formatOptional(before.excludePatterns),
    after: formatOptional(after.excludePatterns),
    changed: (before.excludePatterns ?? null) !== after.excludePatterns
  }];
  return rows.filter(row => row.changed).map(({
    label,
    before: beforeValue,
    after: afterValue,
    multiline
  }) => ({
    label,
    before: beforeValue,
    after: afterValue,
    multiline
  }));
}
function CustomAgentStatusPill(t0) {
  const $ = _c(5);
  const {
    enabled
  } = t0;
  const t1 = enabled ? "success" : "neutral";
  let t2;
  if ($[0] !== enabled) {
    t2 = formatBoolean(enabled);
    $[0] = enabled;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <ProposalStatusPill variant={t1}>{t2}</ProposalStatusPill>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function CustomAgentProposalDetails(t0) {
  const $ = _c(22);
  const {
    proposal
  } = t0;
  if (proposal.action === "create") {
    let t1;
    if ($[0] !== proposal.after.title) {
      t1 = <ProposalDetailRow label="Title" value={proposal.after.title} />;
      $[0] = proposal.after.title;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== proposal.after.description) {
      t2 = <ProposalDetailRow label="Instructions" value={proposal.after.description} />;
      $[2] = proposal.after.description;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== proposal.after.includePatterns) {
      t3 = formatOptional(proposal.after.includePatterns);
      $[4] = proposal.after.includePatterns;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t3) {
      t4 = <ProposalDetailRow label="Include patterns" value={t3} />;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== proposal.after.excludePatterns) {
      t5 = formatOptional(proposal.after.excludePatterns);
      $[8] = proposal.after.excludePatterns;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t5) {
      t6 = <ProposalDetailRow label="Exclude patterns" value={t5} />;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== t1 || $[13] !== t2 || $[14] !== t4 || $[15] !== t6) {
      t7 = <>{t1}{t2}{t4}{t6}</>;
      $[12] = t1;
      $[13] = t2;
      $[14] = t4;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let t1;
  if ($[17] !== proposal.after || $[18] !== proposal.before) {
    t1 = getDiffRows(proposal.before, proposal.after);
    $[17] = proposal.after;
    $[18] = proposal.before;
    $[19] = t1;
  } else {
    t1 = $[19];
  }
  let t2;
  if ($[20] !== t1) {
    t2 = <ProposalDiff rows={t1} />;
    $[20] = t1;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  return t2;
}
export function CustomAgentChangeProposalBlock(t0) {
  const $ = _c(13);
  const {
    proposal
  } = t0;
  const {
    action,
    handleApply,
    handleCancel
  } = useCustomAgentChangeProposalAction(proposal);
  const actionLabel = proposal.action === "create" ? "Create custom agent" : "Update custom agent";
  let t1;
  if ($[0] !== proposal.after.enabled) {
    t1 = <CustomAgentStatusPill enabled={proposal.after.enabled} />;
    $[0] = proposal.after.enabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== proposal) {
    t2 = <CustomAgentProposalDetails proposal={proposal} />;
    $[2] = proposal;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== action || $[5] !== actionLabel || $[6] !== handleApply || $[7] !== handleCancel || $[8] !== proposal.repository.fullName || $[9] !== proposal.warnings || $[10] !== t1 || $[11] !== t2) {
    t3 = <InteractiveProposalCard title={actionLabel} subtitle={proposal.repository.fullName} status={t1} action={action} warnings={proposal.warnings} onApply={handleApply} onCancel={handleCancel} applyTestId="custom-agent-proposal-apply" cancelTestId="custom-agent-proposal-cancel">{t2}</InteractiveProposalCard>;
    $[4] = action;
    $[5] = actionLabel;
    $[6] = handleApply;
    $[7] = handleCancel;
    $[8] = proposal.repository.fullName;
    $[9] = proposal.warnings;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}